import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Send, Megaphone, Loader2, Users, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import ClearTestDataButton from "@/components/admin/ClearTestDataButton";

const MAX_TITLE = 80;
const MAX_BODY = 240;

export default function PushBroadcastPage() {
  const { user } = useAuth();
  const canSend = hasPerm(user, "push_broadcast", "send");
  const [form, setForm] = useState({ title: "", body: "", url: "/student", course_id: "all" });
  const [courses, setCourses] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadHistory = () => api.get("/admin/push/broadcasts").then((r) => setHistory(r.data || []));
  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data || [])).catch(() => {});
    loadHistory();
  }, []);

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    if (form.title.length > MAX_TITLE || form.body.length > MAX_BODY) {
      toast.error(`Title must be ≤${MAX_TITLE} chars, body ≤${MAX_BODY} chars`);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        url: form.url.trim() || "/student",
        course_id: form.course_id === "all" ? null : form.course_id,
      };
      const r = await api.post("/admin/push/broadcast", payload);
      toast.success(r.data?.message || "Broadcast sent");
      setForm({ title: "", body: "", url: "/student", course_id: "all" });
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send broadcast");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="push-broadcast-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-violet-600 flex items-center gap-2">
            <Megaphone className="w-3.5 h-3.5" /> Push Broadcast
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Send a notification</h1>
          <p className="text-slate-600 mt-1 max-w-2xl">Push a message to every student in a course (or all students). Subscribers with active devices will receive it instantly.</p>
        </div>
        <ClearTestDataButton
          role={user?.role}
          title="Clear Broadcast History"
          description="Permanently deletes all broadcast history rows. Active push subscriptions are unaffected."
          endpoint="/admin/push/broadcasts/clear-all"
          confirmPhrase="CLEAR"
          onCleared={() => loadHistory()}
          dataTestId="clear-broadcasts"
        />
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-6 items-start">
        <Card className="p-6 border-slate-200">
          <h3 className="font-display font-bold text-base mb-4 flex items-center gap-2">
            <Send className="w-4 h-4 text-violet-600" /> New Broadcast
          </h3>

          <div className="space-y-4">
            <div>
              <Label>Audience</Label>
              <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                <SelectTrigger data-testid="broadcast-course-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All approved students</SelectItem>
                  {courses.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.course_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Title <span className="text-slate-400 text-xs ml-1">({form.title.length}/{MAX_TITLE})</span></Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value.slice(0, MAX_TITLE) })}
                placeholder="📚 New module released!"
                data-testid="broadcast-title"
              />
            </div>

            <div>
              <Label>Body <span className="text-slate-400 text-xs ml-1">({form.body.length}/{MAX_BODY})</span></Label>
              <Textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value.slice(0, MAX_BODY) })}
                placeholder="Check out the new Pediatric Nursing module — 50 fresh questions, AI explanations included."
                rows={4}
                data-testid="broadcast-body"
              />
            </div>

            <div>
              <Label>Open URL (when tapped) <span className="text-slate-400 text-xs ml-1">optional</span></Label>
              <Input
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="/student"
                data-testid="broadcast-url"
              />
              <p className="text-xs text-slate-500 mt-1">Examples: /student, /student/courses, /student/smart-review</p>
            </div>

            <Button
              onClick={send}
              disabled={busy || !canSend}
              className="bg-violet-700 hover:bg-violet-800"
              data-testid="broadcast-send-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Broadcast
            </Button>
            {!canSend && (
              <p className="text-xs text-slate-500">You don't have permission to send broadcasts.</p>
            )}
          </div>
        </Card>

        <Card className="p-5 border-slate-200">
          <h3 className="font-display font-bold text-sm mb-3">Live Preview</h3>
          <div className="rounded-2xl bg-white border border-slate-200 p-3 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg grid place-items-center text-white shrink-0"
                   style={{ background: "linear-gradient(135deg,#4F46E5,#7C3AED)" }}>P</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] font-semibold text-slate-600">PrepMasters</div>
                  <div className="text-[10px] text-slate-400">now</div>
                </div>
                <div className="font-semibold text-sm text-slate-900 mt-0.5 line-clamp-2">
                  {form.title || "Notification title"}
                </div>
                <div className="text-xs text-slate-600 mt-1 line-clamp-3">
                  {form.body || "Notification body will appear here..."}
                </div>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
            Tapping the notification will open <span className="font-mono">{form.url || "/student"}</span>.
          </p>
        </Card>
      </div>

      <div>
        <h3 className="font-display font-bold text-lg mb-3">Recent Broadcasts</h3>
        <Card className="p-0 border-slate-200 overflow-hidden">
          {history.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="broadcast-empty">No broadcasts sent yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {history.map((b) => (
                <li key={b.id} className="px-5 py-4 flex items-start gap-4" data-testid={`broadcast-row-${b.id}`}>
                  <div className="h-9 w-9 rounded-xl grid place-items-center bg-violet-100 text-violet-700 shrink-0">
                    <Megaphone className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm text-slate-900 truncate">{b.title}</div>
                    <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{b.body}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2 text-[10px]">
                      <Badge variant="outline" className="font-normal">
                        <Users className="w-3 h-3 mr-1" /> {b.recipient_count} student{b.recipient_count === 1 ? "" : "s"}
                      </Badge>
                      <Badge variant="outline" className="font-normal text-emerald-700 border-emerald-200 bg-emerald-50">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> {b.delivered_count} delivered
                      </Badge>
                      {b.course_name && <Badge variant="outline" className="font-normal">{b.course_name}</Badge>}
                      {!b.course_name && <Badge variant="outline" className="font-normal">All courses</Badge>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{(b.created_at || "").slice(0, 10)}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{b.sent_by_name}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
